/**
 * Payment Service
 * Handles payment processing logic (Stripe, Dejavoo Online, manual card processing)
 * Single Responsibility: Payment processing only
 */

/**
 * Creates payment details for Stripe payment
 * @param {Object} stripeForm - Stripe form component reference
 * @returns {Promise<Object>} Payment details with token or error
 */
export async function processStripePayment(stripeForm) {
  if (!stripeForm) {
    throw new Error("Stripe form is required");
  }

  const { token, error } = await stripeForm.createToken();

  if (error) {
    throw new Error(error.message || "Unable to process card");
  }

  if (!token) {
    throw new Error("Failed to create payment token");
  }

  return {
    token: token.id
  };
}

const DEJAVOO_TOKEN_MAX_ATTEMPTS = 3;
const DEJAVOO_TOKEN_RETRY_DELAY_MS = 400;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * FTD payment tokens are single-use and can expire almost immediately if the SDK
 * was not fully ready or the charge is slightly delayed. Retry those cases
 * silently before surfacing an error to the customer.
 */
export function isDejavooRetryableTokenError(message) {
  if (!message) return false;
  const lower = String(message).toLowerCase();

  // Card expiry is a user/card issue, not a transient payment-token issue.
  if (lower.includes("credit card") && lower.includes("expir")) return false;
  if (lower.includes("card has expired")) return false;

  if (lower.includes("payment form is not ready")) return true;
  if (lower.includes("failed to create payment token")) return true;
  if (lower.includes("payment token") && lower.includes("expir")) return true;
  if (lower.includes("token") && lower.includes("expir")) return true;
  if (lower.includes("invalid token")) return true;
  if (lower.includes("token not found")) return true;

  return false;
}

/**
 * Creates payment details for Dejavoo Online (Freedom to Design) payment.
 *
 * Tokenizes the card via the FTD SDK and returns the single-use
 * ``payment_token_id``. The card is NOT charged here: the charge runs
 * server-side in biyo-v2 (the iPOSpays WAF allowlists the server's egress IP),
 * so the charge is atomic with order creation and can't be stranded by a failed
 * follow-up request the way a browser-side charge could. This mirrors how Stripe
 * sends only a ``{ token }`` to the backend.
 *
 * @param {Object} dejavooForm - Dejavoo form component reference
 * @returns {Promise<{payment_token_id: string}>} The single-use payment token
 */
export async function processDejavooPayment(dejavooForm) {
  if (!dejavooForm) {
    throw new Error("Payment form is required");
  }

  let lastError = null;

  for (let attempt = 1; attempt <= DEJAVOO_TOKEN_MAX_ATTEMPTS; attempt++) {
    try {
      const { payment_token_id, error } = await dejavooForm.createToken();

      if (error) {
        const message = error.message || "Unable to process card";
        if (
          isDejavooRetryableTokenError(message) &&
          attempt < DEJAVOO_TOKEN_MAX_ATTEMPTS
        ) {
          console.warn(
            `[Dejavoo] Tokenization failed (attempt ${attempt}), retrying:`,
            message
          );
          lastError = new Error(message);
          await delay(DEJAVOO_TOKEN_RETRY_DELAY_MS);
          continue;
        }
        throw new Error(message);
      }

      if (!payment_token_id) {
        const message = "Failed to create payment token";
        if (attempt < DEJAVOO_TOKEN_MAX_ATTEMPTS) {
          console.warn(
            `[Dejavoo] Missing payment token (attempt ${attempt}), retrying`
          );
          lastError = new Error(message);
          await delay(DEJAVOO_TOKEN_RETRY_DELAY_MS);
          continue;
        }
        throw new Error(message);
      }

      // Hand the single-use token to the backend, which performs the charge.
      return { payment_token_id };
    } catch (error) {
      const message = error?.message || "Unable to process payment";
      if (
        isDejavooRetryableTokenError(message) &&
        attempt < DEJAVOO_TOKEN_MAX_ATTEMPTS
      ) {
        console.warn(
          `[Dejavoo] Tokenization failed (attempt ${attempt}), retrying:`,
          message
        );
        lastError = error instanceof Error ? error : new Error(message);
        await delay(DEJAVOO_TOKEN_RETRY_DELAY_MS);
        continue;
      }
      throw error instanceof Error ? error : new Error(message);
    }
  }

  throw lastError || new Error("Unable to process payment. Please try again.");
}

/**
 * Creates payment details for manual card processing (USAePay, etc.)
 * @param {Object} cardDetails - Card details object
 * @param {string} cardDetails.name - Name on card
 * @param {string} cardDetails.number - Card number (with or without spaces)
 * @param {string} cardDetails.expiration - Expiry date (MM/YY format)
 * @param {string} cardDetails.cvv - CVV code
 * @returns {Object} Payment details object
 */
export function processManualCardPayment(cardDetails) {
  if (
    !cardDetails.name ||
    !cardDetails.number ||
    !cardDetails.expiration ||
    !cardDetails.cvv
  ) {
    throw new Error("All card details are required");
  }

  try {
    return {
      name: cardDetails.name,
      number: parseInt(cardDetails.number.replace(/\s+/g, "")),
      expiration: cardDetails.expiration.replace("/", ""),
      cvv: parseInt(cardDetails.cvv)
    };
  } catch (err) {
    throw new Error("Invalid card details");
  }
}

/**
 * Gets payment details based on payment method
 * @param {Object} params
 * @param {Object} params.paymentMethod - Payment method object
 * @param {Object} params.cardDetails - Card details (for manual processing)
 * @param {Object} params.stripeForm - Stripe form reference (for Stripe)
 * @param {Object} params.dejavooForm - Dejavoo form reference (for Dejavoo Online)
 * @returns {Promise<Object|null>} Payment details or null
 */
export async function getPaymentDetails({
  paymentMethod,
  cardDetails,
  stripeForm,
  dejavooForm
}) {
  if (!paymentMethod || !paymentMethod.card_processing) {
    return null;
  }

  if (paymentMethod.payment_processor === "Stripe") {
    return await processStripePayment(stripeForm);
  } else if (paymentMethod.payment_processor === "Dejavoo Online") {
    return await processDejavooPayment(dejavooForm);
  } else {
    return processManualCardPayment(cardDetails);
  }
}

/**
 * Formats card number with spaces (XXXX XXXX XXXX XXXX)
 * @param {string} value - Raw card number
 * @returns {string} Formatted card number
 */
export function formatCardNumber(value) {
  const cleaned = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
  const matches = cleaned.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || "";
  const parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  return parts.length ? parts.join(" ") : cleaned;
}

/**
 * Formats expiry date (MM/YY)
 * @param {string} value - Raw expiry input
 * @returns {string} Formatted expiry (MM/YY)
 */
export function formatExpiry(value) {
  const cleaned = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

  if (cleaned.length >= 2) {
    return cleaned.substring(0, 2) + "/" + cleaned.substring(2, 4);
  }

  return cleaned;
}

/**
 * Validates card expiry format (like root version)
 * @param {string} value - Expiry input
 * @returns {string} Formatted expiry (MM/YY)
 */
export function validateCardExpiry(value) {
  const expiry = value.replace(/\//g, "");
  if (expiry.match(/^[0-9]{2}$/g)) {
    return expiry + "/";
  }
  return formatExpiry(value);
}
